/**
 * 소명 페이지 공용 UI 헬퍼 — Defense.js _spaInit 에서 분리 (Phase2-A).
 *   showToast / showOv(BS shim) / showLoading / showPhotoGallery / openPhotoUpload.
 *
 * 전역($j, jQuery $, document) 의존만 사용 — 상태 S 비참조(순수 UI).
 * 함수 본문은 Defense.js 원본과 동일(동작·HTML 출력 보존), 모듈 최상위로 들여쓰기만 정리.
 */

// 토스트 — 공용 $j.Ui.Toast 사용 (자체 .toast-pop 대체). 원본과 동일하게 하단 배치.
export function showToast(msg) { $j.Ui.Toast.show(msg, { place: 'bottom', offset: 90 }); }  // 하단 고정버튼 위

// 모달 — 공용 엔진 $j.Ui.Modal(sheet 하단시트) 사용.
//   onBind 호환: ov.find(sel)=콘텐츠 내 탐색, ov.remove()=닫기 → 기존 호출처 무변경.
//   단일 인스턴스 유지(기존 BS 동작) — 새 시트를 열면 직전 시트를 먼저 닫는다(스택 X).
//   ⚠️ 모달은 <body>에 렌더(= #b-defense 밖)되므로 콘텐츠에 .dv-scope 를 직접 부여한다.
//   그래야 _defense.scss 의 devil 클래스(.inp/.lbl/.sec-t-lg 등)가 시트 안에 적용됨.
let _ovHandle = null;
export function showOv(title, body, onBind, opts = {}) {
    const prev = _ovHandle;
    _ovHandle = null;
    if (prev) prev.close();   // 직전 시트 닫기(배경 스크롤락 해제 포함)

    const m = $j.Ui.Modal.open({
        title: title || '', mode: 'sheet', variant: 'devil', dismissible: false, // BS 동작 유지 — 백드롭 탭 닫힘 없음(폼 유실 방지), X 로만 닫기
        onClose: () => {
            if (_ovHandle === m) _ovHandle = null;
            if (opts.onClose) opts.onClose();   // 이 시트가 닫힌 뒤 할 일(예: 동의 시트 → 폼 복원). Ui.Modal 은 바디 잠금을 풀고 나서 부른다
        },
    });
    _ovHandle = m;
    const $c = m.el.find('.ui-modal__body').addClass('dv-scope');
    $c.html(body);
    if (onBind) onBind({ find: (sel) => $c.find(sel), remove: () => m.close() });
    return m;
}

/**
 * 개인정보 활용 동의 시트 — 제보 화면(Pages/Devil/Report.js bindAgree → $j.F.Devil.Terms.viewConsent)과 같은 흐름.
 * 체크박스를 바로 켤 수 없고, 이 시트를 열어 끝까지 본 뒤 '동의합니다' 를 눌러야 켜진다(2026-09-18 기획 — 제보 화면과 통일).
 *
 * - 본문은 DB 약관이 아니라 소명·정정 전용 고지(항목·목적·기간·거부권)다. 비회원 화면이라 회원용 약관 API
 *   (/b/ajax/settings/terms, G5_DEVIL 인증)를 못 쓴다 — 문안은 옛 '자세히 보기'(Defense.js) 것을 그대로 옮겼다.
 * - 폼 시트와 이 시트는 같은 단일 인스턴스(showOv)라 열면 폼이 닫힌다 → 닫힐 때(동의·X 모두) opts.onClose 로 폼을
 *   다시 띄운다. 입력값은 상태 S 에 있어 그대로 복원된다.
 * - 끝까지 읽음 판정·잠금 버튼은 Terms.bindConsentFoot 하나를 쓴다(복제하지 않음). 스크롤러는 Ui.Modal 본문(.ui-modal__body).
 *
 * @param {boolean} isNotme  정정(본인 아님) 폼이면 true, 소명 폼이면 false — 항목·목적 문구가 다르다
 * @param {{agreed?:boolean, onAgree?:Function, onClose?:Function}} opts
 */
export function openPrivacyConsent(isNotme, opts = {}) {
    // 본문 우선순위: 관리자 약관(정정 BD_NOTME_PRIVACY, /adm/config/terms 에서 편집) → 없거나 요청 실패면 아래 고정 문안(2026-09-30 이전 방식).
    //   비로그인 화면이라 정정 전용 엔드포인트를 쓴다. (isNotme 인자는 호환용 — 소명 폼은 2026-10 폐지, 정정만 남음)
    const items   = '이름, 연락처, 증빙 서류, 정정 요청 사유';
    const purpose = '본인 아님 확인 및 검색 노출 예외 처리, 본인 확인';
    const denied  = '동의를 거부할 권리가 있으며, 거부 시 정정 요청 진행이 불가합니다.';
    const sec = (t, b) => `<div><p style="font-size:14px;font-weight:700;color:var(--t1);margin-bottom:8px">${t}</p><p>• ${b}</p></div>`;
    const fallbackDoc = `<div style="display:flex;flex-direction:column;gap:16px;font-size:13px;color:var(--t2);line-height:1.7">
          ${sec('1. 수집·이용 항목', items)}
          ${sec('2. 이용 목적', purpose)}
          ${sec('3. 보유·이용 기간', '본인 확인 완료 즉시 폐기')}
          ${sec('4. 동의 거부 권리', denied)}
        </div>`;
    const open = (doc) => {
        const html = `<div class="tm-doc">${doc}</div>
        <div class="tm-consent"><button type="button" class="tm-consent__btn is-locked">끝까지 읽어주세요 <span aria-hidden="true">↓</span></button></div>`;
        const m = showOv('개인정보 활용 동의', html, null, { onClose: opts.onClose });
        $j.F.Devil.Terms.bindConsentFoot(m.el.find('.ui-modal__body'), () => m.close(), {
            agreed:  !!opts.agreed,
            onAgree: () => { if (opts.onAgree) opts.onAgree(); },
        });
        return m;
    };
    // 서버 본문을 받은 뒤 한 번만 연다 — 열고 나서 갈아끼우면 bindConsentFoot 의 끝까지-읽음 판정이 어긋난다.
    $.ajax({ url: '/b/defense/ajax/terms/notme', type: 'GET', dataType: 'json', timeout: 3000 })
        .then((res) => (res && res.result && res.body) ? res.body : null, () => null)
        .then((body) => open(body || fallbackDoc));
}

export function showLoading(msg) {
    const ov = $(`<div class="ov" style="display:flex;align-items:center;justify-content:center">
      <div style="text-align:center">
        <div class="dv-spin" style="margin:0 auto 16px"></div>
        <p style="font-size:15px;font-weight:700;color:#fff">${msg || '처리 중...'}</p>
      </div>
    </div>`);
    $('#dv-app').append(ov);
    return ov;
}

export function showPhotoGallery(slides, startIndex) {
    if (!slides || !slides.length) return;
    let current = Math.max(0, Math.min(startIndex || 0, slides.length - 1));
    const total = slides.length;
    const ov = $(`<div class="ov" style="background:rgba(0,0,0,.92);backdrop-filter:none;display:flex;flex-direction:column">
      <div style="height:56px;padding:0 16px;display:flex;align-items:center;justify-content:space-between;flex-shrink:0;margin-top:38px">
        <span class="pg-counter" style="font-size:14px;font-weight:700;color:#fff">${current + 1} / ${total}</span>
        <button class="pg-close" style="width:32px;height:32px;border-radius:8px;border:none;background:rgba(255,255,255,.15);cursor:pointer;display:flex;align-items:center;justify-content:center"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="#fff" stroke-width="2" stroke-linecap="round"/></svg></button>
      </div>
      <div class="pg-wrap" style="flex:1;overflow:hidden;position:relative;display:flex">
        <div class="pg-track" style="display:flex;width:100%;transition:transform .35s cubic-bezier(.25,1,.5,1)">
          ${slides.map(s => `<div style="flex:0 0 100%;display:flex;align-items:center;justify-content:center;padding:20px">${s}</div>`).join('')}
        </div>
        ${total > 1 ? `<button class="pg-prev" style="position:absolute;left:12px;top:50%;transform:translateY(-50%);width:40px;height:40px;border-radius:20px;background:rgba(255,255,255,.18);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;z-index:2"><svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="#fff" stroke-width="2.2" stroke-linecap="round"/></svg></button>
        <button class="pg-next" style="position:absolute;right:12px;top:50%;transform:translateY(-50%);width:40px;height:40px;border-radius:20px;background:rgba(255,255,255,.18);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;z-index:2"><svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M5 2l5 5-5 5" stroke="#fff" stroke-width="2.2" stroke-linecap="round"/></svg></button>` : ''}
      </div>
      ${total > 1 ? `<div style="padding:16px 0 40px;display:flex;justify-content:center;gap:8px;flex-shrink:0">
        ${slides.map((_, i) => `<button class="pg-dot" data-i="${i}" style="width:8px;height:8px;border-radius:4px;background:${i === current ? '#fff' : 'rgba(255,255,255,.3)'};border:none;cursor:pointer;padding:0;transition:.2s"></button>`).join('')}
      </div>` : '<div style="height:60px;flex-shrink:0"></div>'}
    </div>`);
    function goTo(i) {
        current = Math.max(0, Math.min(i, total - 1));
        ov.find('.pg-track').css('transform', `translateX(-${current * 100}%)`);
        ov.find('.pg-counter').text(`${current + 1} / ${total}`);
        ov.find('.pg-dot').each(function (idx) { $(this).css('background', idx === current ? '#fff' : 'rgba(255,255,255,.3)'); });
        ov.find('.pg-prev').css({ opacity: current === 0 ? '.3' : '1', 'pointer-events': current === 0 ? 'none' : 'auto' });
        ov.find('.pg-next').css({ opacity: current === total - 1 ? '.3' : '1', 'pointer-events': current === total - 1 ? 'none' : 'auto' });
    }
    ov.find('.pg-close').on('click', () => { $(document).off('mouseup.pg touchend.pg'); ov.remove(); });
    ov.find('.pg-prev').on('click', () => goTo(current - 1));
    ov.find('.pg-next').on('click', () => goTo(current + 1));
    ov.find('.pg-dot').on('click', function () { goTo(+$(this).data('i')); });
    // 스와이프
    let startX = 0, dragging = false;
    ov.find('.pg-wrap').on('touchstart mousedown', function (e) {
        if ($(e.target).closest('.pg-prev,.pg-next,.pg-close').length) return;
        startX = e.originalEvent.touches ? e.originalEvent.touches[0].pageX : e.pageX;
        dragging = true;
        ov.find('.pg-track').css('transition', 'none');
    });
    $(document).off('mouseup.pg touchend.pg').on('mouseup.pg touchend.pg', function (e) {
        if (!dragging) return;
        dragging = false;
        const endX = e.originalEvent.changedTouches ? e.originalEvent.changedTouches[0].pageX : (e.pageX || startX);
        ov.find('.pg-track').css('transition', 'transform .35s cubic-bezier(.25,1,.5,1)');
        const d = endX - startX;
        if (d < -40) goTo(current + 1);
        else if (d > 40) goTo(current - 1);
        else goTo(current);
    });
    ov.find('.pg-track').css('transform', `translateX(-${current * 100}%)`);
    ov.find('.pg-prev').css({ opacity: current === 0 ? '.3' : '1', 'pointer-events': current === 0 ? 'none' : 'auto' });
    ov.find('.pg-next').css({ opacity: current === total - 1 ? '.3' : '1', 'pointer-events': current === total - 1 ? 'none' : 'auto' });
    $('#dv-app').append(ov);
}

// + 버튼 핸들러 — 공용 모듈 호출(앱=네이티브 피커 / 외부브라우저=파일input 폴백).
//   엔드포인트는 제보와 동일한 /api/upload (G5_API 인증 비강제 → 비회원 소명자도 사용 가능).
//   target(=S.somyungPhotos/S.notmePhotos) 길이로 남은 장수 계산 후 피커 오픈. 누적은 EventBus 핸들러(Defense.js)에서.
export function openPhotoUpload(context, target) {
    const remain = 10 - target.length;
    if (remain <= 0) { showToast('사진은 최대 10장까지 등록할 수 있습니다.'); return; }
    $j.F.Upload.addPhotoHere({ tag: 'defense', context, max_sheet: remain });
}
